import {Button, ButtonGroup, Card, FormControl, FormField} from 'platform/components';
import {Box, HStack, VStack} from 'platform/foundation';

import {useFieldArray, useWatch} from 'react-hook-form';

import {BranchResponseBody} from '@dms/api/shared';
import i18n from '@dms/i18n';

import {suffixTestId, TestIdProps} from 'shared/web';

import {TestDriveCalendarSettingsFormValues} from '../schemas/getTestDriveCalendarSettingsFormSchema';
import {getVehicleConditionOptions} from '../utils/getVehicleConditionOptions';

interface TestDriveCalendarBranchOverridesProps extends TestIdProps {
  control: FormControl<TestDriveCalendarSettingsFormValues>;
  assignedBranches: BranchResponseBody[];
}

export function TestDriveCalendarBranchOverrides(props: TestDriveCalendarBranchOverridesProps) {
  const conditionOptions = getVehicleConditionOptions();

  const {fields, append, remove} = useFieldArray<TestDriveCalendarSettingsFormValues>({
    control: props.control,
    name: 'branchOverrides',
  });

  const branchOverrides = useWatch({control: props.control, name: 'branchOverrides'});
  const branchIds = useWatch({control: props.control, name: 'branchIds'}) ?? [];

  return (
    <VStack spacing={4}>
      {fields.map((field, index) => {
        const branchIdUsedElsewhere = new Set(
          (branchOverrides ?? [])
            .filter((_, otherIndex) => otherIndex !== index)
            .map((override) => override.branchId)
        );
        const branchOptions = props.assignedBranches
          .filter((branch) => branchIds.includes(branch.id))
          .filter((branch) => !branchIdUsedElsewhere.has(branch.id))
          .map((branch) => ({value: branch.id, label: branch.name}));

        return (
          <Card variant="inlineGrey" key={field.id}>
            <HStack spacing={4} align="flex-start">
              <Box minWidth={60}>
                <FormField
                  control={props.control}
                  type="choice"
                  name={`branchOverrides.${index}.branchId`}
                  label={i18n.t('entity.branch.labels.branch')}
                  options={branchOptions}
                  menuInPortal
                  isNotClearable
                  isRequired
                  data-testid={suffixTestId(`branchOverrides-${index}-branchId`, props)}
                />
              </Box>
              <FormField
                control={props.control}
                type="chips"
                isMultiple
                isRequired
                name={`branchOverrides.${index}.vehicleConditions`}
                label={i18n.t('page.testDrive.labels.categories')}
                options={conditionOptions}
                data-testid={suffixTestId(`branchOverrides-${index}-vehicleConditions`, props)}
              />
              <Box paddingTop={5}>
                <Button
                  title={i18n.t('general.actions.delete')}
                  leftIcon="action/delete"
                  variant="errorLink"
                  onClick={() => remove(index)}
                  data-testid={suffixTestId(`branchOverrides-${index}-remove`, props)}
                />
              </Box>
            </HStack>
          </Card>
        );
      })}
      <ButtonGroup align="left">
        <Button
          title={i18n.t('page.testDrive.actions.addBranchOverride')}
          leftIcon="content/add_circle"
          variant="link"
          onClick={() => append({branchId: '', vehicleConditions: []})}
          data-testid={suffixTestId('branchOverrides-add', props)}
        />
      </ButtonGroup>
    </VStack>
  );
}
